/*
 *  Copyright
 *
 *  Tegona S.A.
 *
 *  Tegona JS © 2017, Tegona S.A.
 *
 *  ALL RIGHTS RESERVED. THIS PROGRAM CONTAINS MATERIAL PROTECTED
 *  UNDER INTERNATIONAL AND SWITZERLAND COPYRIGHT LAWS AND TREATIES.
 *  ANY UNAUTHORIZED USE OF THE PROGRAM, INCLUDING REPRODUCTION,
 *  MODIFICATION, TRANSFER, TRANSMITTAL OR REPUBLICATION OF THIS
 *  MATERIAL IN ANY FORM OR BY ANY MEANS IS PROHIBITED WITHOUT
 *  SPECIFIC WRITTEN PERMISSION OF THE COPYRIGHT HOLDER.
 *
 *  copyright@tegona.com
 */

import React from 'react'
import moment from 'moment'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { injectIntl } from 'react-intl'
import autoBind from 'react-autobind'
import { CircularProgress } from 'material-ui'
import { pink600 } from 'material-ui/styles/colors'

import { FixedPanel, Panel } from 'generic/panels'
import Card from 'generic/card/Card'

import Base from 'components/common/Base'
import List from 'components/common/prestation/List'
import Detail from 'components/applicant/prestation/dashboard/Detail'

import * as actions from 'actions/ApplicantPrestation'
import * as constants from 'constants/Prestation'

const VIEWPORT_HEIGHT = '79vh'
const ELEMENT_HEIGHT = 78
// Interval in milliseconds
const REFRESH_PRESTATION_INTERVAL = 600000

class DashboardPage extends React.Component {
  constructor (props) {
    super(props)

    autoBind(this)

    this.state = {
      requestPending: false,
      loading: false,
      detailLoading: false,
      prestations: []
    }

    this.previousOffset = 0
    this.interval = null
    this.scrollDiv = null
  }

  componentDidMount () {
    this.setState({
      loading: true,
    })

    const query = {
      date_lte: moment().format('YYYY-MM-DD HH:mm:ss'),
      sort: '-ordered_start_time',
    }

    this.handleLoadPrestations(query)
      .then((count) => {
        if(count < 10) {
          const query = {
            sort: '-ordered_start_time',
          }

          return this.handleLoadPrestations(query)
        }

        return count
      })
      .finally(() => {
        this.setState({
          loading: false,
        })

        this.setRefreshViewPort()

        if (this.scrollDiv) {
          this.scrollDiv.scrollTop = this.previousOffset + 1
          this.previousOffset += 1
        }
      })
  }

  componentWillUnmount () {
    if (this.scrollDiv) {
      this.scrollDiv.removeEventListener('scroll', this.handleScroll)
    }
    if (this.interval) {
      clearInterval(this.interval)
    }

    this._isMounted = false
  }

  handleLoadPrestations(params) {
    const query = {
      ...params,
      page_size: 20,
      objects_count: false,
    }

    return actions.getPrestations(query)
      .then((result) => {
        const objects = result.data.objects.objects

        this.setState(prev => {
          if (!query.date_lte && !query.date_gte) return { prestations: objects }

          const newObjects = objects.filter(
            obj => !prev.prestations.find(p => p.id === obj.id)
          )

          if (query.date_lte) return { prestations: [...prev.prestations, ...newObjects] }
          if (query.date_gte) return { prestations: [...newObjects.reverse(), ...prev.prestations] }

          return null
        })

        return objects.length
      })
  }

  handleUpdatedPrestation (prestation, options={}) {
    this.setState(prev => ({
      prestations: prev.prestations.map(p =>
        p.uid !== prestation.uid ? p : { ...p, ...options }
      )
    }))

    actions.getPrestationUid(prestation.uid)
      .then((result) => {
        const obj = result.data.object

        this.setState(prev => ({
          prestations: prev.prestations.map(p =>
            p.uid !== obj.uid ? p : {
              ...obj,
              is_cancelled_lately: p.is_cancelled_lately,
              is_interpreter_proposals_refused: p.is_interpreter_proposals_refused,
              ...options,
            }
          )
        }))
      })
  }

  setRefreshViewPort () {
    if (this.interval) {
      clearInterval(this.interval)
    }

    this.interval = setInterval(() => {
      if (!this.state.prestations.length) {
        return
      }

      const startIndex = Math.floor(this.scrollDiv.scrollTop / ELEMENT_HEIGHT)
      let endIndex = startIndex + ELEMENT_HEIGHT
      if (endIndex >= this.state.prestations.length) {
        endIndex = this.state.prestations.length - 1
      }

      const dateLt = this.state.prestations[startIndex].start_time
      const dateGt = this.state.prestations[endIndex].start_time

      const query = {
        sort: '-ordered_start_time',
        date_lte: dateLt,
        date_gte: dateGt,
        objects_count: false,
      }

      actions.getPrestations(query)
        .then((result) => {
          let updatedPrestations = result.data.objects.objects
          let existingPrestations = this.state.prestations

          // update existing prestations in store based on api result and inject the new ones
          updatedPrestations.forEach((updated) => {
            let index = existingPrestations.findIndex((p1) => p1.uid === updated.uid)
            if (index > -1) {
              existingPrestations[index] = updated
            } else {
              existingPrestations.push(updated)
            }
          })

          // sort the injected prestations in the list
          existingPrestations = existingPrestations.sort((p1, p2) => {
            if (moment(p1.start_time).isBefore(moment(p2.start_time))) {
              return 1
            } else if (moment(p1.start_time).isAfter(moment(p2.start_time))) {
              return -1
            }

            return p2.uid.localeCompare(p1.uid)
          })


          this.setState({
            prestations: existingPrestations
          })
        })
    }, REFRESH_PRESTATION_INTERVAL)
  }

  handleScroll (e) {
    const offset = e.target.scrollTop
    const maxSize = e.target.scrollHeight
    const layoutSize = e.target.offsetHeight

    if (this.state.requestPending) {
      this.previousOffset = offset
      return
    }

    let query
    let requestPending

    if (offset > this.previousOffset && offset + layoutSize + 200 > maxSize) {
      requestPending = 'bottom'
      const lastPrestation = this.state.prestations.slice(-1)[0]

      const dateTime = moment(lastPrestation.start_time)
      dateTime.set({minutes: dateTime.minute() - 1})

      query = {
        sort: '-ordered_start_time',
        date_lte: dateTime.format('YYYY-MM-DD HH:mm:ss'),
      }
    } else if (offset < this.previousOffset && offset < 200) {
      requestPending = 'top'

      const firstPrestation = this.state.prestations.length
        ? this.state.prestations.slice(0)[0]
        : {start_time: moment().format('YYYY-MM-DD HH:mm:ss')}

      query = {
        sort: 'ordered_start_time',
        date_gte: firstPrestation.start_time,
      }
    }

    if (query) {
      this.setState({requestPending: requestPending})
      let totalElements = 0

      this.handleLoadPrestations(query)
        .then((result) => {
          totalElements = result
        })
        .finally(() => {
          if (this.scrollDiv && requestPending === 'top') {
            const count = totalElements - 1
            this.scrollDiv.scrollTop = this.previousOffset + (count * ELEMENT_HEIGHT)
          }

          this.setState({requestPending: false})
        })
    }

    this.previousOffset = offset
  }

  setScrollRef (scrollDiv) {
    this.scrollDiv = scrollDiv
    if (this.scrollDiv) {
      this.scrollDiv.addEventListener('scroll', this.handleScroll)
    }
  }

  render () {
    return (
      <Base
        page={0}
      >
        <FixedPanel ratio='2:1'>
          <Panel>
            <Card title={this.props.intl.formatMessage(constants.messages.FEED_TITLE)}>
              <div
                style={{
                  height: VIEWPORT_HEIGHT,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  marginBottom: 10
                }}
                ref={this.setScrollRef}
              >
                {
                  this.state.loading
                    ? <div style={{textAlign: 'center', paddingTop: 100}}>
                      <CircularProgress color={pink600} size={100} thickness={5} />
                    </div>
                    : <React.Fragment>
                      <div style={{height: 20}}>
                        {
                          this.state.requestPending === 'top'
                            ? <CircularProgress size={20} style={{paddingLeft: '50%'}} />
                            : null
                        }
                      </div>

                      <List
                        height={ELEMENT_HEIGHT}
                        prestations={this.state.prestations}
                        setLoading={() => this.setState(prev => ({ detailLoading: !prev.detailLoading }))}
                        loading={this.state.detailLoading}
                      />

                      <div>
                        {
                          this.state.requestPending === 'bottom'
                            ? <CircularProgress size={20} style={{paddingLeft: '50%'}} />
                            : null
                        }
                      </div>
                    </React.Fragment>
                }
              </div>
            </Card>
          </Panel>

          <Panel>
            <Detail
              prestation={this.props.selectedPrestation}
              handleUpdatedPrestation={this.handleUpdatedPrestation}
              loading={this.state.detailLoading}
            />
          </Panel>
        </FixedPanel>
      </Base>
    )
  }
}

const mapStateToProps = (state) => ({
  selectedPrestation: state.ApplicantPrestation.selectedPrestation
})

const mapActionsToProps = (dispatch) => ({
  actions: bindActionCreators(actions, dispatch)
})

export default connect(mapStateToProps, mapActionsToProps)(injectIntl(DashboardPage))



// WEBPACK FOOTER //
// ./src/components/pages/DashboardPage.js